<style scoped>
.not-booked {
    opacity: 0.5;
}
.number-circle {
    border: 2px solid var(--z-primary);
    color: var(--z-primary);
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    background-color: white;
    box-sizing: content-box;
}
.text-shadow {
    text-shadow: 0 0 3px #000;
}
.team-image {
    width: 75px;
    height: 75px;
}
@media screen and (min-width: 450px) {
    .team-image {
        width: 100px;
        height: 100px;
    }
}

@media (max-width: 768px) {
    .center-part {
        max-width: calc(100vw - 209px);
    }
}
</style>

<template>
    <div>
        <div class="title">{{$translate('Next match')}}</div>
        <v-card v-if="match" class="pb-1">
            <div class="d-flex flex-row">
                <div class="d-flex align-center justify-center" v-if="twoTeams && opponent">
                    <img :src="'/api/public/image/get?size=200x200&type=team&id=' + opponent.id" class="ma-2 team-image" />
                </div>
                <div class="d-flex flex-column ma-3 flex-grow-1 center-part">
                    <div class="font-weight-bold" v-if="match.name">{{match.name}}</div>
                    <div class="font-weight-bold" v-if="twoTeams && opponent">{{opponent.name}}</div>
                    <div>
                        <v-icon small>mdi-calendar</v-icon>
                        {{Date.formatFullLong(match.activity.startTime, !$vuetify.breakpoint.mobile).capitalize()}}
                    </div>
                    <div v-if="location">
                        <v-icon small>mdi-map-marker</v-icon>
                        {{location}}
                    </div>
                    <a href="" @click.prevent="gotoMatch()">{{$translate('Go to match page')}}</a>
                </div>
                <teamsport-book-match v-if="ourTeam.group" :match="match" :bookingOption="getBookingOption()" :teamId="teamId" class="justify-center" :class="$vuetify.breakpoint.mobile ? 'mr-1' : 'mr-4'" />
            </div>

            <v-divider></v-divider>

            <!-- Player list & add / call player-->
            <div v-if="ourTeam.group" class="d-grid ma-3 align-center">

                <PlayerFormationWithCaptain :players="ourPlayersInMatch" :team="ourTeam" :match="match" :showCaptain="requireCaptain" />

                <!-- Add / call player button & dialog -->
                <div v-if="this.isTeamLeader" class="d-flex z-clickable" key="add" @click="addPlayer" v-ripple="{center: true}">
                    <div class="mr-2">
                        <v-icon color="primary">mdi-plus-circle</v-icon>
                    </div>
                    <div>
                        <div>{{$translate('Add / call players')}}</div>
                    </div>
                </div>
                <teamsport-invite-players-dialog v-model="showInvitePlayersDialog" :match="match" :teamId="teamId" :group="ourGroup" :activity="match.team_activity" />
            </div>

        </v-card>
        <v-card v-else-if="!match && !loading">
            <v-card-text>
                {{$translate('No matches scheduled')}}
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import TeamsportBookMatch from '../TeamsportBookMatch.vue'
import TeamsportInvitePlayersDialog from '../../components/Teamsport/InvitePlayersDialog.vue';
import PlayerFormationWithCaptain from '../../components/Teamsport/PlayerFormationWithCaptain.vue';

export default {
    name: 'zoezi-teamsport-next-match',

    zoezi: {
        title: 'Next match',
        icon: 'mdi-calendar-clock',
        addon: 'TeamSports'
    },

    props: {
        teamId: {
            title: 'Registration ID',
            type: Number
        }
    },

    components: {
        TeamsportBookMatch,
        TeamsportInvitePlayersDialog,
        PlayerFormationWithCaptain
    },

    data: () => ({
        match: null,
        formation: null,
        loading: false,
        groupId: null,
        unsubscribe: [],
        showInvitePlayersDialog: false,
    }),

    watch: {
        teamId: {
            immediate: true,
            handler() {
                this.getNextMatch();
            }
        },
        // Make sure that shirt numbers set in team management are always up to date
        '$store.state.user'() {
            this.getNextMatch();
        }
    },

    computed: {
        ourTeam() {
            if (!this.match) {
                return null;
            }
            return this.match.teams.find(t => t.id == this.teamId);
        },
        ourPlayers() {
            if (!this.ourTeam || !this.formation) {
                return [];
            }

            const teamResponsible = this.ourTeam.group.responsible || [];

            return this.ourTeam.group.users.map(user_id => {
                const user = this.formation.find(x => x.id == user_id);
                if (!user) {
                    return null;
                }
                return {
                    name: user.name,
                    id: user_id,
                    shirt_number: this.getUserShirtNumber(this.ourTeam.id, user.id),
                    status: user.status,
                    booked: user.status == 'booked',
                    invited: user.status == 'invited',
                    declined: user.status == 'declined',
                    membership: user.membership,
                    leader: teamResponsible.includes(user.id),
                    captain: user.id == this.match?.team_captains[this.ourTeam.id],
                }
            }).filter(x => x).sort((a, b) => a.name.localeCompare(b.name));
        },
        ourPlayersInMatch() {
            return this.ourPlayers.filter(x => x.status != 'not invited');
        },
        opponents() {
            if (!this.match) {
                return [];
            }
            return this.match.teams.filter(t => t.id != this.teamId);
        },
        opponent() {
            if (this.opponents.length == 0) {
                return null;
            }
            return this.opponents[0];
        },
        location() {
            if (!this.match) {
                return null;
            }
            let workout = this.match.activity;
            if (workout.location || workout.room) {
                return (workout.room ? workout.room.name : '') + (workout.location ? ', ' + workout.location.name : '');
            }
            return null;
        },
        ourGroup() {
            return this.$store.state.user.groups.find(g => g.id == this.groupId);
        },
        isTeamLeader() {
            return this.ourGroup?.responsible?.includes(this.$store.state.user.id);
        },
        leaderPresent() {
            return this.ourPlayersInMatch.find(p => p.leader);
        },
        requireCaptain() {
            return this.match?.team_captain && this.ourPlayersInMatch.length > 1 && !this.leaderPresent;
        },
        workoutIds() {
            if (!this.match) {
                return [];
            }
            return this.match.team_activities;
        },
        twoTeams() {
            return this.match?.teams?.length == 2;
        },
        captain() {
            return this.ourPlayersInMatch.find(p => p.captain);
        }
    },

    methods: {
        getUserShirtNumber(teamId, userId) {
            let match_number = this.match.shirt_numbers?.find(shirt => shirt.user == userId && shirt.team == teamId);
            let group_number = this.match.teams.find(team => team.id == teamId)?.group?.shirt_numbers?.find(user => user.user_id == userId);
            return match_number?.shirt || group_number?.shirt || null;
        },
        getFormation() {
            if (!this.groupId) {
                return;
            }

            this.loading = true;
            this.$api.get('/api/teamsport/match/team/formation/status', { match: this.match.id, group: this.groupId }).then(formation => {
                this.formation = formation;
                this.loading = false
            });
        },
        getNextMatch() {
            this.loading = true;
            this.$api.get('/api/memberapi/teamsport/match/next', {team_id: this.teamId}).then(result => {
                let match = null;
                if (result && result.matches && result.matches.length > 0) {
                    match = result.matches[0];
                }
                this.groupId = match?.teams.find(t => t.id == this.teamId)?.group?.id;
                this.match = match;
                if (match?.activity) {
                    this.$booking.transformWorkout(this.match.activity);
                }

                if (this.groupId) {
                    this.getFormation();
                } else {
                    this.loading = false
                }
            });
        },
        addPlayer() {
            this.showInvitePlayersDialog = true;
        },
        gotoMatch() {
            const matchPage = this.$root.siteconfig?.pages?.match;
            this.$root.followLink(matchPage, this.match.id);
        },
        setCaptain(player) {
            // Only team leaders can set captains
            if (!this.isTeamLeader) {
                // Notify user that they are not allowed to set captains
                this.$store.commit('showSnackBar', {
                    text: this.$translate('Only team leaders can set the team captain'),
                    color: 'error'
                });
                return;
            }

            // Set user as captain
            this.$api.post('/api/memberapi/teamsport/match/team/captain/set', {
                matchId: this.match.id,
                teamId: this.ourTeam.id,
                captainId: player.id,
            });
        },
        getBookingOption() {
            return this.ourGroup?.bookingOption;
        }
    },
    destroyed() {
        this.unsubscribe.forEach(x => x());
    },
    mounted() {
        this.unsubscribe.push(this.$api.subscribe({type: 'workoutbooking', callback: (_, event) => {
            if (event.data.find(item => this.workoutIds.includes(item.workout))) {
                this.getFormation();
            }
        }}));
        this.unsubscribe.push(this.$api.subscribe({type: 'workoutbookinginvite', callback: (_, event) => {
            if (event.data.find(item => this.workoutIds.includes(item.workout_id))) {
                this.getFormation();
            }
        }}));
        this.unsubscribe.push(this.$api.subscribe({type: 'teamsportmatch', callback: (_, event) => {
            if (this.match && event.data.find(x => x.id == this.match.id)) {
                this.getNextMatch();
            }
        }}))
    }
}
</script>
